"use client";

// Import icons from react-feather (remove CloudSun and SunDim)
import {
  Activity,
  Users,
  BarChart2 as BarChartIcon, // Alias for your icon usage
  Sun,
  Home,
  Battery,
  Calendar,
  Settings,
  Menu,
  Cloud,    // Using Cloud instead of CloudSun
  Zap,
  Moon      // Optionally import Moon if you want an alternative for SunDim
} from "react-feather";

// Import charts from recharts
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  AreaChart,
  Area,
  BarChart,
  Bar,
  ResponsiveContainer
} from "recharts";

import { useState, useEffect } from "react";

import Button from "./ui/Button";
import Card from "./ui/Card";
import Progress from "./ui/Progress";
import Tabs from "./ui/Tabs";
import "./Dashboard.css";

// Mock data for charts
const hourlyData = [
  { time: "00:00", production: 0, consumption: 2 },
  { time: "01:00", production: 0, consumption: 1.8 },
  { time: "02:00", production: 0, consumption: 1.5 },
  { time: "03:00", production: 0, consumption: 1.3 },
  { time: "04:00", production: 0, consumption: 1.2 },
  { time: "05:00", production: 0.2, consumption: 1.2 },
  { time: "06:00", production: 1.5, consumption: 1.5 },
  { time: "07:00", production: 3.2, consumption: 2.1 },
  { time: "08:00", production: 5.8, consumption: 2.3 },
  { time: "09:00", production: 7.9, consumption: 2.5 },
  { time: "10:00", production: 9.3, consumption: 2.8 },
  { time: "11:00", production: 10.2, consumption: 3.0 },
  { time: "12:00", production: 10.8, consumption: 3.2 },
  { time: "13:00", production: 10.5, consumption: 3.0 },
  { time: "14:00", production: 9.8, consumption: 2.8 },
  { time: "15:00", production: 8.5, consumption: 2.7 },
  { time: "16:00", production: 6.9, consumption: 2.9 },
  { time: "17:00", production: 4.2, consumption: 3.5 },
  { time: "18:00", production: 2.1, consumption: 4.2 },
  { time: "19:00", production: 0.8, consumption: 4.5 },
  { time: "20:00", production: 0, consumption: 4.0 },
  { time: "21:00", production: 0, consumption: 3.5 },
  { time: "22:00", production: 0, consumption: 3.0 },
  { time: "23:00", production: 0, consumption: 2.5 },
];

const dailyData = [
  { day: "Mon", production: 85.2, consumption: 62.5 },
  { day: "Tue", production: 78.9, consumption: 60.8 },
  { day: "Wed", production: 92.3, consumption: 63.2 },
  { day: "Thu", production: 68.5, consumption: 61.9 },
  { day: "Fri", production: 88.7, consumption: 64.3 },
  { day: "Sat", production: 95.1, consumption: 58.7 },
  { day: "Sun", production: 97.8, consumption: 56.4 },
];

const monthlyData = [
  { month: "Jan", production: 1250, efficiency: 72 },
  { month: "Feb", production: 1580, efficiency: 75 },
  { month: "Mar", production: 2120, efficiency: 82 },
  { month: "Apr", production: 2540, efficiency: 85 },
  { month: "May", production: 2980, efficiency: 88 },
  { month: "Jun", production: 3250, efficiency: 92 },
  { month: "Jul", production: 3420, efficiency: 94 },
  { month: "Aug", production: 3280, efficiency: 91 },
  { month: "Sep", production: 2850, efficiency: 87 },
  { month: "Oct", production: 2320, efficiency: 83 },
  { month: "Nov", production: 1780, efficiency: 78 },
  { month: "Dec", production: 1350, efficiency: 74 },
];

function Dashboard() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState("hourly");
  const [currentData, setCurrentData] = useState({
    production: 8.7,
    dailyEnergy: 58.2,
    efficiency: 92,
    weather: {
      condition: "Partly Cloudy",
      temperature: 24,
      wind: 8,
      humidity: 45,
      uvIndex: 6,
      irradiance: 780,
    },
  });

  // Simulate real-time data updates
  useEffect(() => {
    const interval = setInterval(() => {
      // Simulate small random changes to create a "live" effect
      const randomChange = () => (Math.random() - 0.5) * 0.2;

      setCurrentData((prev) => ({
        ...prev,
        production: Number.parseFloat((prev.production + randomChange()).toFixed(1)),
        dailyEnergy: Number.parseFloat((prev.dailyEnergy + randomChange() * 2).toFixed(1)),
        efficiency: Math.min(100, Math.max(0, Number.parseInt((prev.efficiency + randomChange()).toFixed(0)))),
        weather: {
          ...prev.weather,
          temperature: Number.parseFloat((prev.weather.temperature + randomChange()).toFixed(1)),
          wind: Number.parseFloat((prev.weather.wind + randomChange()).toFixed(1)),
          humidity: Math.min(100, Math.max(0, Number.parseInt((prev.weather.humidity + randomChange() * 2).toFixed(0)))),
          irradiance: Number.parseInt((prev.weather.irradiance + randomChange() * 10).toFixed(0)),
        },
      }));
    }, 5000); // Update every 5 seconds

    return () => clearInterval(interval);
  }, []);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  return (
    <div className="dashboard">
      {/* Sidebar */}
      {/*  */}

      {/* Main Content */}
      <main className="main-content">
        <header className="main-header">
          {/* <Button variant="icon" onClick={toggleSidebar} aria-label="Toggle Sidebar">
            <Menu size={20} />
          </Button> */}
          <h1>Solar Plant Dashboard</h1>
          <div className="header-actions">
            <Button variant="outline">
              <Cloud size={16} /> {/* Replaced CloudSun with Cloud */}
              <span>Weather</span>
            </Button>
            <Button variant="outline">
              <Settings size={16} />
            </Button>
          </div>
        </header>

        <div className="dashboard-content">
          {/* Stats Cards */}
          <div className="stats-grid">
            <Card className="stat-card">
              <div className="card-header">
                <h3>Current Production</h3>
                <Sun className="card-icon sun" />
              </div>
              <div className="card-content">
                <div className="stat-value">{currentData.production} kW</div>
                <p className="stat-change">+12% from last hour</p>
                <div className="progress-container">
                  <div className="progress-bar">
                    <div className="progress-fill sun" style={{ width: "65%" }}></div>
                  </div>
                  <p className="progress-text">65% of peak capacity</p>
                </div>
              </div>
            </Card>

            <Card className="stat-card">
              <div className="card-header">
                <h3>Today's Energy</h3>
                <Zap className="card-icon energy" />
              </div>
              <div className="card-content">
                <div className="stat-value">{currentData.dailyEnergy} kWh</div>
                <p className="stat-change">+8% from yesterday</p>
                <div className="progress-container">
                  <div className="progress-labels">
                    <span>0 kWh</span>
                    <span>100 kWh</span>
                  </div>
                  <Progress value={currentData.dailyEnergy} max={100} />
                </div>
              </div>
            </Card>

            <Card className="stat-card">
              <div className="card-header">
                <h3>System Efficiency</h3>
                <Sun className="card-icon efficiency" /> {/* Replaced SunDim with Sun */}
              </div>
              <div className="card-content">
                <div className="stat-value">{currentData.efficiency}%</div>
                <p className="stat-change">+2% from last week</p>
                <div className="progress-container">
                  <div className="progress-labels">
                    <span>Poor</span>
                    <span>Excellent</span>
                  </div>
                  <Progress value={currentData.efficiency} max={100} className="efficiency-progress" />
                </div>
              </div>
            </Card>

            <Card className="stat-card">
              <div className="card-header">
                <h3>Weather Condition</h3>
                <Cloud className="card-icon weather" />
              </div>
              <div className="card-content">
                <div className="weather-info">
                  <Cloud className="weather-icon" /> {/* Replaced CloudSun with Cloud */}
                  <div className="weather-condition">{currentData.weather.condition}</div>
                </div>
                <p className="weather-details">
                  {currentData.weather.temperature}°C, Wind: {currentData.weather.wind} km/h
                </p>
                <div className="weather-stats">
                  <div className="weather-stat">
                    <span className="weather-stat-label">Humidity</span>
                    <span className="weather-stat-value">{currentData.weather.humidity}%</span>
                  </div>
                  <div className="weather-stat">
                    <span className="weather-stat-label">UV Index</span>
                    <span className="weather-stat-value">{currentData.weather.uvIndex}/10</span>
                  </div>
                  <div className="weather-stat">
                    <span className="weather-stat-label">Irradiance</span>
                    <span className="weather-stat-value">{currentData.weather.irradiance} W/m²</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Charts Section */}
          <div className="charts-section">
            <Tabs
              tabs={[
                { id: "hourly", label: "Hourly" },
                { id: "daily", label: "Daily" },
                { id: "monthly", label: "Monthly" },
              ]}
              activeTab={activeTab}
              onChange={setActiveTab}
              actions={
                <div className="tab-actions">
                  <Button variant="outline">
                    <Calendar size={16} />
                    <span>Date Range</span>
                  </Button>
                  <Button variant="outline">Export</Button>
                </div>
              }
            />

            <div className="tab-content">
              {activeTab === "hourly" && (
                <div className="hourly-tab">
                  <Card className="chart-card">
                    <div className="card-header">
                      <h3>Hourly Energy Production & Consumption</h3>
                      <p className="card-description">
                        Today's solar energy production and consumption in kilowatts (kW)
                      </p>
                    </div>
                    <div className="card-content chart-container">
                      <ResponsiveContainer width="100%" height={400}>
                        <AreaChart data={hourlyData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="time" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Area
                            type="monotone"
                            dataKey="production"
                            stackId="1"
                            stroke="#f59e0b"
                            fill="#f59e0b"
                            name="Production"
                          />
                          <Area
                            type="monotone"
                            dataKey="consumption"
                            stackId="2"
                            stroke="#3b82f6"
                            fill="#3b82f6"
                            name="Consumption"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </Card>

                  <div className="detail-cards">
                    <Card className="detail-card">
                      <div className="card-header">
                        <h3>Energy Balance</h3>
                        <p className="card-description">Net energy production vs consumption</p>
                      </div>
                      <div className="card-content">
                        <div className="energy-balance">
                          <div className="balance-item">
                            <div className="balance-value production">75.2 kWh</div>
                            <div className="balance-label">Production</div>
                          </div>
                          <div className="balance-item">
                            <div className="balance-value consumption">58.6 kWh</div>
                            <div className="balance-label">Consumption</div>
                          </div>
                          <div className="balance-item">
                            <div className="balance-value net">+16.6 kWh</div>
                            <div className="balance-label">Net Balance</div>
                          </div>
                        </div>
                        <div className="grid-export">
                          <div className="export-header">
                            <span>Grid Export</span>
                            <span>16.6 kWh</span>
                          </div>
                          <Progress value={22} max={100} />
                          <p className="export-text">22% of production exported to grid</p>
                        </div>
                      </div>
                    </Card>

                    <Card className="detail-card">
                      <div className="card-header">
                        <h3>Performance Metrics</h3>
                        <p className="card-description">Key performance indicators</p>
                      </div>
                      <div className="card-content">
                        <div className="performance-metrics">
                          <div className="metric">
                            <div className="metric-label">Peak Power</div>
                            <div className="metric-value">10.8 kW</div>
                            <div className="metric-detail">at 12:00 PM</div>
                          </div>
                          <div className="metric">
                            <div className="metric-label">Performance Ratio</div>
                            <div className="metric-value">0.89</div>
                            <div className="metric-detail">Excellent</div>
                          </div>
                          <div className="metric">
                            <div className="metric-label">CO₂ Saved</div>
                            <div className="metric-value">38.2 kg</div>
                            <div className="metric-detail">Today</div>
                          </div>
                          <div className="metric">
                            <div className="metric-label">Yield</div>
                            <div className="metric-value">5.4 kWh/kWp</div>
                            <div className="metric-detail">Daily specific yield</div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>
              )}

              {activeTab === "daily" && (
                <div className="daily-tab">
                  <Card className="chart-card">
                    <div className="card-header">
                      <h3>Daily Energy Production & Consumption</h3>
                      <p className="card-description">
                        This week's solar energy production and consumption in kilowatt-hours (kWh)
                      </p>
                    </div>
                    <div className="card-content chart-container">
                      <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={dailyData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="day" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="production" name="Production" fill="#f59e0b" />
                          <Bar dataKey="consumption" name="Consumption" fill="#3b82f6" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </Card>

                  <div className="detail-cards">
                    <Card className="detail-card">
                      <div className="card-header">
                        <h3>Weekly Summary</h3>
                        <p className="card-description">Total energy production and efficiency</p>
                      </div>
                      <div className="card-content">
                        <div className="energy-balance">
                          <div className="balance-item">
                            <div className="balance-value production">606.5 kWh</div>
                            <div className="balance-label">Total Production</div>
                          </div>
                          <div className="balance-item">
                            <div className="balance-value consumption">427.8 kWh</div>
                            <div className="balance-label">Total Consumption</div>
                          </div>
                        </div>
                        <div className="weekly-target">
                          <div className="target-header">
                            <span>Weekly Target</span>
                            <span>600 kWh</span>
                          </div>
                          <Progress value={101} max={100} />
                          <p className="target-text">101% of weekly target achieved</p>
                        </div>
                      </div>
                    </Card>

                    <Card className="detail-card">
                      <div className="card-header">
                        <h3>Weather Impact</h3>
                        <p className="card-description">Weather conditions affecting production</p>
                      </div>
                      <div className="card-content">
                        <div className="weather-impact">
                          <div className="weather-day">
                            <Sun className="weather-day-icon sun" />
                            <div>
                              <div className="weather-day-type">Sunny Days</div>
                              <div className="weather-day-count">4 days</div>
                            </div>
                          </div>
                          <div className="weather-day">
                            <Cloud className="weather-day-icon partly-cloudy" /> {/* Replaced CloudSun with Cloud */}
                            <div>
                              <div className="weather-day-type">Partly Cloudy</div>
                              <div className="weather-day-count">2 days</div>
                            </div>
                          </div>
                          <div className="weather-day">
                            <Cloud className="weather-day-icon cloudy" />
                            <div>
                              <div className="weather-day-type">Cloudy</div>
                              <div className="weather-day-count">1 day</div>
                            </div>
                          </div>
                          <div className="weather-impact-metric">
                            <div className="weather-impact-label">Weather Efficiency Impact</div>
                            <div className="weather-impact-value">-8%</div>
                            <div className="weather-impact-detail">Due to cloud cover</div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>
              )}

              {activeTab === "monthly" && (
                <div className="monthly-tab">
                  <Card className="chart-card">
                    <div className="card-header">
                      <h3>Monthly Energy Production & Efficiency</h3>
                      <p className="card-description">
                        This year's solar energy production in kilowatt-hours (kWh) and system efficiency
                      </p>
                    </div>
                    <div className="card-content chart-container">
                      <ResponsiveContainer width="100%" height={400}>
                        <LineChart data={monthlyData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis yAxisId="left" />
                          <YAxis yAxisId="right" orientation="right" domain={[0, 100]} />
                          <Tooltip />
                          <Legend />
                          <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="production"
                            stroke="#f59e0b"
                            name="Production (kWh)"
                            strokeWidth={2}
                          />
                          <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey="efficiency"
                            stroke="#10b981"
                            name="Efficiency (%)"
                            strokeWidth={2}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </Card>

                  <div className="detail-cards">
                    <Card className="detail-card">
                      <div className="card-header">
                        <h3>Annual Summary</h3>
                        <p className="card-description">Year-to-date performance metrics</p>
                      </div>
                      <div className="card-content">
                        <div className="annual-summary">
                          <div>
                            <div className="summary-label">Total Energy Production</div>
                            <div className="summary-value">28,720 kWh</div>
                            <div className="summary-detail">Year to date</div>
                          </div>
                          <div className="summary-metrics">
                            <div className="metric">
                              <div className="metric-label">Average Monthly</div>
                              <div className="metric-value">2,393 kWh</div>
                            </div>
                            <div className="metric">
                              <div className="metric-label">CO₂ Saved</div>
                              <div className="metric-value">14.3 tons</div>
                            </div>
                            <div className="metric">
                              <div className="metric-label">Peak Month</div>
                              <div className="metric-value">July</div>
                              <div className="metric-detail">3,420 kWh</div>
                            </div>
                            <div className="metric">
                              <div className="metric-label">Annual Target</div>
                              <div className="metric-value">82%</div>
                              <div className="metric-detail">Of 35,000 kWh</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>

                    <Card className="detail-card">
                      <div className="card-header">
                        <h3>System Health</h3>
                        <p className="card-description">Overall system performance and maintenance</p>
                      </div>
                      <div className="card-content">
                        <div className="system-health">
                          <div className="system-status">
                            <div className="status-label">System Status</div>
                            <div className="status-value">
                              <span className="status-indicator optimal"></span>
                              <span>Optimal</span>
                            </div>
                          </div>
                          <div className="health-metrics">
                            <div className="health-metric">
                              <div className="metric-header">
                                <span>Inverter Efficiency</span>
                                <span>98%</span>
                              </div>
                              <Progress value={98} max={100} />
                            </div>
                            <div className="health-metric">
                              <div className="metric-header">
                                <span>Panel Degradation</span>
                                <span>0.5%</span>
                              </div>
                              <Progress value={0.5} max={100} />
                            </div>
                            <div className="health-metric">
                              <div className="metric-header">
                                <span>Next Maintenance</span>
                                <span>23 days</span>
                              </div>
                              <Progress value={77} max={100} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default Dashboard;
